/**
 * OpenLayers Map Host — 맵 인스턴스·레이어·플러그인 생명주기를 한곳에서 관리.
 * Vue 컴포넌트·다른 맵(DotSolar, ImageAlignment 등)은 Host를 받아 기능(Plugin)만 붙인다.
 */
export class OpenLayersMapHost {
  /**
   * @param {object} p
   * @param {import('ol/Map').default} p.map
   * @param {import('ol/View').default} p.view
   * @param {string} p.targetId
   * @param {import('ol/source/Vector').default} [p.vectorSource]
   * @param {import('ol/layer/Vector').default} [p.vectorLayer]
   * @param {Record<string, import('ol/layer/Layer').default>} [p.namedLayers]
   */
  constructor({ map, view, targetId, vectorSource, vectorLayer, namedLayers = {} }) {
    this.map = map;
    this.view = view;
    this.targetId = targetId;
    this.vectorSource = vectorSource ?? null;
    this.vectorLayer = vectorLayer ?? null;
    this._namedLayers = { ...namedLayers };
    this._plugins = new Map();
    this._pluginCleanup = new Map();
  }

  getMap() {
    return this.map;
  }

  getView() {
    return this.view;
  }

  getVectorSource() {
    return this.vectorSource;
  }

  getVectorLayer() {
    return this.vectorLayer;
  }

  getLayer(name) {
    return this._namedLayers[name] ?? null;
  }

  getViewport() {
    return this.map?.getViewport?.() ?? null;
  }

  /**
   * @param {{ id: string, attach: (host: OpenLayersMapHost, ctx?: object) => (void|(() => void)), detach?: (host: OpenLayersMapHost) => void }} plugin
   * @param {object} [ctx] — Vue 컴포넌트·스토어 등 플러그인 컨텍스트
   */
  attachPlugin(plugin, ctx = {}) {
    if (!plugin?.id || typeof plugin.attach !== 'function') {
      return;
    }
    this.detachPlugin(plugin.id);
    const cleanup = plugin.attach(this, ctx);
    this._plugins.set(plugin.id, plugin);
    if (typeof cleanup === 'function') {
      this._pluginCleanup.set(plugin.id, cleanup);
    }
  }

  detachPlugin(pluginId) {
    const plugin = this._plugins.get(pluginId);
    const cleanup = this._pluginCleanup.get(pluginId);
    if (typeof cleanup === 'function') {
      cleanup();
    } else if (plugin?.detach) {
      plugin.detach(this);
    }
    this._plugins.delete(pluginId);
    this._pluginCleanup.delete(pluginId);
  }

  detachAllPlugins() {
    [...this._plugins.keys()].forEach(id => this.detachPlugin(id));
  }

  destroy() {
    this.detachAllPlugins();
    if (this.map) {
      this.map.setTarget(null);
    }
    this.map = null;
    this.view = null;
    this.vectorSource = null;
    this.vectorLayer = null;
    this._namedLayers = {};
  }
}
